import { AlertCircle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { mapErrorToUserMessage } from '@/lib/error-messages';
import type { TaskTrackingSummary } from '@/lib/types';

interface CampaignFailedBannerProps {
  campaignCreationSummary: TaskTrackingSummary;
  errorMessage?: string | null;
  onRestart: () => void;
  isRestarting: boolean;
  restartAttempt?: number;
  maxAttempts?: number;
}

export function CampaignFailedBanner({
  campaignCreationSummary,
  errorMessage,
  onRestart,
  isRestarting,
  restartAttempt = 0,
  maxAttempts = 5,
}: CampaignFailedBannerProps) {
  const hasFailed = campaignCreationSummary.failed > 0;
  const isStuck = (campaignCreationSummary.stuck_in_progress ?? 0) > 0;
  const isActivelyRestarting =
    campaignCreationSummary.in_progress > 0 &&
    (campaignCreationSummary.restart_attempt_count ?? 0) > 0;

  // Hide banner if campaign is actively restarting (has in-progress tasks after a restart attempt)
  if (isActivelyRestarting) {
    return null;
  }

  // Only show if campaign creation has failed or is stuck (and not actively restarting)
  if (!hasFailed && !isStuck) {
    return null;
  }

  const title = isStuck ? 'Campaign Stuck Building' : 'Campaign Creation Failed';
  const userFriendlyError = isStuck
    ? 'This campaign has been building for longer than expected. You can restart it to try again.'
    : mapErrorToUserMessage(errorMessage);
  const hasReachedLimit = restartAttempt >= maxAttempts;

  return (
    <div className="bg-[#FEE2E2] border-l-4 border-l-[#FCA5A5] rounded-lg p-6">
      <div className="flex items-center gap-4">
        <div className="bg-white rounded-lg shadow p-3 self-start mt-0.5">
          <AlertCircle className="h-6 w-6 text-[#B91C1C]" />
        </div>
        <div className="flex flex-1 items-center justify-between gap-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
            <p className="text-gray-900 mb-2">{userFriendlyError}</p>
            {restartAttempt > 0 && !hasReachedLimit && (
              <p className="text-sm text-gray-900">
                Previous attempts: {restartAttempt} of {maxAttempts}
              </p>
            )}
            {hasReachedLimit && (
              <p className="text-sm text-gray-900">
                This campaign has reached the maximum number of restart attempts. Please contact support for assistance.
              </p>
            )}
          </div>
          <div className="shrink-0">
            {hasReachedLimit ? (
              <Button
                variant="outline"
                size="lg"
                onClick={() => {
                  window.location.href = 'mailto:support@glitchads.com';
                }}
                className="w-[280px] py-6 shadow-lg flex items-center justify-center space-x-2"
              >
                <span>CONTACT SUPPORT</span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                size="lg"
                onClick={onRestart}
                disabled={isRestarting}
                className="w-[280px] py-6 bg-[#290BD9] hover:bg-[#2309C5] text-white shadow-lg flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span>{isRestarting ? 'RESTARTING CAMPAIGN...' : 'RESTART CAMPAIGN'}</span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
